import first from 'lodash/first'
import map from 'lodash/map'
import { NuqsAdapter } from 'nuqs/adapters/next/pages'
import React, { FC, useMemo } from 'react'

import { ProductListBlockV2 } from '@Blocks/ProductList/v2'
import { ProductListDisplaySettingValue, ProductListProps } from '@Components/ProductList'
import { BlockTypeWithGlobal, ProductListBlock as ProductListBlockGraphQL, RteField } from '@Generated/graphql'
import { ProductSearchProvider, ProductSearchProviderProps } from '@Globals/Algolia/Context'
import { parseHitsPerPage, processURLSearchParams } from '@Globals/Algolia/Shared'
import { PageProductListBlock } from '@Globals/Algolia/Types'
import { useBlockConfig } from '@Globals/CMS/Context'
import { convertRTEToHTML, getPageReferences } from '@Globals/CMS/FieldHelpers'

interface ProductListBlockProps {
  id?: string
  productListBlock: ProductListBlockGraphQL
}

const getBlockData = (productListBlock: ProductListBlockGraphQL, usePagination: boolean): Omit<ProductListProps & ProductSearchProviderProps, 'id'> => {
  const {
    description,
    display,
    extraBlocks,
    facetConfig,
    facets,
    filter,
    filters,
    guidedFilters,
    header,
    injections,
    limit,
    settings: settingsRaw,
    topLinks,
  } = productListBlock as PageProductListBlock
  const settings = typeof settingsRaw === 'object' ? settingsRaw || {} : {}

  const topLinkData = map(topLinks, (link) => {
    const page = first(getPageReferences(link.references || []))
    return {
      ...link,
      title: page?.title || '',
      type: page?.type || '',
      pageID: page?.id || '',
    }
  })

  return {
    description: convertRTEToHTML(description as RteField),
    display: (display || []) as ProductListDisplaySettingValue[],
    enableDescription: 'description' in settings ? Boolean(settings.description) : false,
    enableDescriptionShowMore: 'descriptionShowMore' in settings ? Boolean(settings.descriptionShowMore) : true,
    enableGrouping: 'groups' in settings ? Boolean(settings.groups) : true,
    extraBlocks: (extraBlocks || []) as BlockTypeWithGlobal[],
    facetConfig,
    facets: facets || ['*'],
    filters: filters || filter || '',
    guidedFilters: guidedFilters || null,
    injections: injections || null,
    title: header || '',
    hitsPerPage: parseHitsPerPage(limit, usePagination).toString(),
    topLinks: topLinkData || [],
  }
}

const ProductListBlock: FC<ProductListBlockProps> = ({ id, productListBlock }) => {
  const config = useBlockConfig()
  const usePagination = Boolean(config.algolia?.usePagination)
  const data = useMemo(() => getBlockData(productListBlock, usePagination), [productListBlock, usePagination])

  return (
    <NuqsAdapter processUrlSearchParams={processURLSearchParams}>
      <ProductSearchProvider
        algoliaConfig={config?.algolia}
        extraBlocks={data.extraBlocks}
        extraBlocksIDPrefix={id}
        facetConfig={data.facetConfig}
        facets={data.facets}
        filters={data.filters}
        guidedFilters={data.guidedFilters}
        hitsPerPage={data.hitsPerPage}
        injections={data.injections}
        title={data.title}
      >
        <ProductListBlockV2
          description={data.description}
          enableDescription={data.enableDescription}
          enableDescriptionShowMore={data.enableDescriptionShowMore}
          id={id}
          topLinks={data.topLinks}
        />
      </ProductSearchProvider>
    </NuqsAdapter>
  )
}

export default ProductListBlock
